/**
 * VorgaengePage — Vorgangs- & Projektmanagement
 * 
 * DESIGN: Mikro-minimalistisch, 2-Spalten (Liste links, Detail rechts)
 * 
 * Features:
 * - Übersicht aller Vorgänge mit Status-Badges
 * - Filter nach Status, Kategorie, Priorität
 * - Neuen Vorgang erstellen (Modal)
 * - Detail-Ansicht mit Tabs: Übersicht, Aufgaben, Dokumente, Notizen
 * - Verknüpfungen zu Dokumenten, Aufgaben, Kontakten
 * - Aktivitätslog / Notizen-Timeline
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useVorgaenge,
  useVorgangVerknuepfungen,
  useVorgangNotizen,
  VORGANG_STATUS,
  VORGANG_KATEGORIEN,
  VORGANG_PRIORITAETEN,
  NOTIZ_TYPEN,
  getStatusInfo,
  getKategorieInfo,
  getPrioritaetInfo,
  type Vorgang,
  type NeuerVorgang,
} from '@/hooks/useVorgaenge';
import {
  Briefcase, Plus, Search, Filter, ChevronRight, Calendar, Clock,
  AlertTriangle, CheckCircle, Pause, Archive, FileText, ListTodo,
  MessageSquare, Link2, X, Save, Trash2, Edit3, Phone, Mail,
  Users, MoreVertical, ArrowRight, Flag,
} from 'lucide-react';
import { toast } from 'sonner';

// ==================== HELPER ====================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `vor ${days} Tagen`;
  return formatDate(dateStr);
}

// ==================== STATUS ICON ====================

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'offen': return <AlertTriangle className="w-4 h-4 text-blue-500" />;
    case 'in_bearbeitung': return <Clock className="w-4 h-4 text-yellow-500" />;
    case 'warten': return <Pause className="w-4 h-4 text-orange-500" />;
    case 'abgeschlossen': return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'archiviert': return <Archive className="w-4 h-4 text-gray-400" />;
    default: return <AlertTriangle className="w-4 h-4 text-gray-400" />;
  }
}

// ==================== NEUER VORGANG MODAL ====================

function NeuerVorgangModal({ onClose, onSave }: { onClose: () => void; onSave: (v: NeuerVorgang) => void }) {
  const [form, setForm] = useState<NeuerVorgang>({
    titel: '',
    beschreibung: '',
    kategorie: 'sonstiges',
    prioritaet: 'mittel',
    business_unit: 'gutachter_koop',
  });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Neuer Vorgang</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
            <input
              type="text"
              value={form.titel}
              onChange={e => setForm(f => ({ ...f, titel: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="z.B. Rückforderung Steuerberater 2025"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
            <textarea
              value={form.beschreibung || ''}
              onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              rows={3}
              placeholder="Worum geht es? Was ist das Ziel?"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
              <select
                value={form.kategorie}
                onChange={e => setForm(f => ({ ...f, kategorie: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
              >
                {VORGANG_KATEGORIEN.map(k => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priorität</label>
              <select
                value={form.prioritaet}
                onChange={e => setForm(f => ({ ...f, prioritaet: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
              >
                {VORGANG_PRIORITAETEN.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frist</label>
              <input
                type="date"
                value={form.frist || ''}
                onChange={e => setForm(f => ({ ...f, frist: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Betrag (EUR)</label>
              <input
                type="number"
                value={form.betrag || ''}
                onChange={e => setForm(f => ({ ...f, betrag: e.target.value ? Number(e.target.value) : undefined }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                placeholder="0,00"
                step="0.01"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Abbrechen</button>
          <button
            onClick={() => { if (form.titel.trim()) onSave(form); }}
            disabled={!form.titel.trim()}
            className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Erstellen
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== VORGANG DETAIL ====================

function VorgangDetail({ vorgang, onUpdate, onDelete, onClose }: {
  vorgang: Vorgang;
  onUpdate: (id: string, updates: Partial<Vorgang>) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  onClose: () => void;
}) {
  const [activeTab, setActiveTab] = useState<'uebersicht' | 'notizen'>('uebersicht');
  const { notizen, addNotiz } = useVorgangNotizen(vorgang.id);
  const [neueNotiz, setNeueNotiz] = useState('');
  const [notizTyp, setNotizTyp] = useState('notiz');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    titel: vorgang.titel,
    beschreibung: vorgang.beschreibung || '',
    status: vorgang.status,
    prioritaet: vorgang.prioritaet,
    kategorie: vorgang.kategorie,
    frist: vorgang.frist ? vorgang.frist.split('T')[0] : '',
    wiedervorlage_datum: vorgang.wiedervorlage_datum ? vorgang.wiedervorlage_datum.split('T')[0] : '',
    wiedervorlage_notiz: vorgang.wiedervorlage_notiz || '',
    betrag: vorgang.betrag,
  });

  const statusInfo = getStatusInfo(vorgang.status);
  const kategorieInfo = getKategorieInfo(vorgang.kategorie);
  const prioritaetInfo = getPrioritaetInfo(vorgang.prioritaet);

  const handleSaveEdit = async () => {
    const success = await onUpdate(vorgang.id, {
      titel: editForm.titel,
      beschreibung: editForm.beschreibung || null,
      status: editForm.status,
      prioritaet: editForm.prioritaet,
      kategorie: editForm.kategorie,
      frist: editForm.frist || null,
      wiedervorlage_datum: editForm.wiedervorlage_datum || null,
      wiedervorlage_notiz: editForm.wiedervorlage_notiz || null,
      betrag: editForm.betrag,
    });
    if (success) {
      toast.success('Vorgang aktualisiert');
      setEditing(false);
    }
  };

  const handleAddNotiz = async () => {
    if (!neueNotiz.trim()) return;
    const success = await addNotiz(neueNotiz.trim(), notizTyp);
    if (success) {
      setNeueNotiz('');
      toast.success('Notiz hinzugefügt');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Vorgang wirklich löschen? Alle Verknüpfungen und Notizen werden ebenfalls gelöscht.')) return;
    const success = await onDelete(vorgang.id);
    if (success) {
      toast.success('Vorgang gelöscht');
      onClose();
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    const success = await onUpdate(vorgang.id, {
      status: newStatus,
      ...(newStatus === 'abgeschlossen' ? { abgeschlossen_am: new Date().toISOString() } : {}),
    });
    if (success) {
      await addNotiz(
        `Status geändert: ${getStatusInfo(vorgang.status).label} → ${getStatusInfo(newStatus).label}`,
        'status_aenderung'
      );
      toast.success('Status aktualisiert');
    }
  };

  const tabs = [
    { id: 'uebersicht', label: 'Übersicht', icon: Briefcase },
    { id: 'notizen', label: `Notizen (${notizen.length})`, icon: MessageSquare },
  ] as const;

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 border-b px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {editing ? (
              <input
                type="text"
                value={editForm.titel}
                onChange={e => setEditForm(f => ({ ...f, titel: e.target.value }))}
                className="text-xl font-bold w-full px-2 py-1 border rounded-lg"
              />
            ) : (
              <h2 className="text-xl font-bold text-gray-900 truncate">{vorgang.titel}</h2>
            )}
            <div className="flex items-center gap-3 mt-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                <StatusIcon status={vorgang.status} />
                {statusInfo.label}
              </span>
              <span className="text-xs text-gray-500">{kategorieInfo.label}</span>
              <span className={`text-xs font-medium ${prioritaetInfo.color}`}>
                <Flag className="w-3 h-3 inline mr-0.5" />{prioritaetInfo.label}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 ml-3">
            {editing ? (
              <>
                <button onClick={handleSaveEdit} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg" title="Speichern">
                  <Save className="w-4 h-4" />
                </button>
                <button onClick={() => setEditing(false)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg" title="Abbrechen">
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg" title="Bearbeiten">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={handleDelete} className="p-2 text-red-400 hover:bg-red-50 rounded-lg" title="Löschen">
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg md:hidden">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Status Quick-Actions */}
        <div className="flex gap-2 mt-3">
          {VORGANG_STATUS.filter(s => s.value !== vorgang.status && s.value !== 'archiviert').map(s => (
            <button
              key={s.value}
              onClick={() => handleStatusChange(s.value)}
              className={`text-xs px-2.5 py-1 rounded-full border hover:shadow-sm transition-all ${s.color}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 border-b px-6">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'uebersicht' && (
          <div className="space-y-6">
            {/* Beschreibung */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Beschreibung</h3>
              {editing ? (
                <textarea
                  value={editForm.beschreibung}
                  onChange={e => setEditForm(f => ({ ...f, beschreibung: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows={4}
                />
              ) : (
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {vorgang.beschreibung || 'Keine Beschreibung'}
                </p>
              )}
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="text-xs text-gray-500 block mb-1">Frist</span>
                {editing ? (
                  <input type="date" value={editForm.frist} onChange={e => setEditForm(f => ({ ...f, frist: e.target.value }))} className="text-sm border rounded px-2 py-1 w-full" />
                ) : (
                  <span className="text-sm font-medium">{formatDate(vorgang.frist)}</span>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="text-xs text-gray-500 block mb-1">Wiedervorlage</span>
                {editing ? (
                  <input type="date" value={editForm.wiedervorlage_datum} onChange={e => setEditForm(f => ({ ...f, wiedervorlage_datum: e.target.value }))} className="text-sm border rounded px-2 py-1 w-full" />
                ) : (
                  <span className="text-sm font-medium">{formatDate(vorgang.wiedervorlage_datum)}</span>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="text-xs text-gray-500 block mb-1">Betrag</span>
                {editing ? (
                  <input type="number" value={editForm.betrag || ''} onChange={e => setEditForm(f => ({ ...f, betrag: e.target.value ? Number(e.target.value) : null }))} className="text-sm border rounded px-2 py-1 w-full" step="0.01" />
                ) : (
                  <span className="text-sm font-medium">
                    {vorgang.betrag ? `${Number(vorgang.betrag).toLocaleString('de-DE', { minimumFractionDigits: 2 })} ${vorgang.waehrung}` : '—'}
                  </span>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="text-xs text-gray-500 block mb-1">Erstellt am</span>
                <span className="text-sm font-medium">{formatDate(vorgang.created_at)}</span>
              </div>
            </div>

            {/* Kategorie & Priorität bearbeiten */}
            {editing && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Kategorie</label>
                  <select value={editForm.kategorie} onChange={e => setEditForm(f => ({ ...f, kategorie: e.target.value }))} className="text-sm border rounded px-2 py-1 w-full">
                    {VORGANG_KATEGORIEN.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Priorität</label>
                  <select value={editForm.prioritaet} onChange={e => setEditForm(f => ({ ...f, prioritaet: e.target.value }))} className="text-sm border rounded px-2 py-1 w-full">
                    {VORGANG_PRIORITAETEN.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* Wiedervorlage-Notiz */}
            {(vorgang.wiedervorlage_notiz || editing) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Wiedervorlage-Notiz</h3>
                {editing ? (
                  <textarea
                    value={editForm.wiedervorlage_notiz}
                    onChange={e => setEditForm(f => ({ ...f, wiedervorlage_notiz: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    rows={2}
                    placeholder="Was soll bei der Wiedervorlage beachtet werden?"
                  />
                ) : (
                  <p className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    {vorgang.wiedervorlage_notiz}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'notizen' && (
          <div className="space-y-4">
            {/* Neue Notiz */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <select
                  value={notizTyp}
                  onChange={e => setNotizTyp(e.target.value)}
                  className="text-xs border rounded px-2 py-1"
                >
                  {NOTIZ_TYPEN.filter(t => t.value !== 'system' && t.value !== 'status_aenderung').map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <textarea
                value={neueNotiz}
                onChange={e => setNeueNotiz(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                rows={3}
                placeholder="Notiz, Telefonat-Ergebnis, E-Mail-Zusammenfassung..."
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleAddNotiz}
                  disabled={!neueNotiz.trim()}
                  className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Hinzufügen
                </button>
              </div>
            </div>

            {/* Notizen-Timeline */}
            <div className="space-y-3">
              {notizen.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-8">Noch keine Notizen vorhanden</p>
              )}
              {notizen.map(notiz => {
                const typInfo = NOTIZ_TYPEN.find(t => t.value === notiz.typ);
                const isSystem = notiz.typ === 'status_aenderung' || notiz.typ === 'system';
                return (
                  <div key={notiz.id} className={`border rounded-lg p-3 ${isSystem ? 'bg-gray-50 border-gray-200' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {notiz.typ === 'telefonat' && <Phone className="w-3.5 h-3.5 text-blue-500" />}
                        {notiz.typ === 'email' && <Mail className="w-3.5 h-3.5 text-purple-500" />}
                        {notiz.typ === 'meeting' && <Users className="w-3.5 h-3.5 text-green-500" />}
                        {notiz.typ === 'notiz' && <MessageSquare className="w-3.5 h-3.5 text-gray-500" />}
                        {notiz.typ === 'status_aenderung' && <ArrowRight className="w-3.5 h-3.5 text-orange-500" />}
                        <span className="text-xs font-medium text-gray-500">{typInfo?.label || 'Notiz'}</span>
                      </div>
                      <span className="text-xs text-gray-400">{timeAgo(notiz.created_at)}</span>
                    </div>
                    <p className={`text-sm whitespace-pre-wrap ${isSystem ? 'text-gray-500 italic' : 'text-gray-700'}`}>
                      {notiz.inhalt}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== VORGANG KARTE ====================

function VorgangKarte({ vorgang, isSelected, onClick }: {
  vorgang: Vorgang;
  isSelected: boolean;
  onClick: () => void;
}) {
  const statusInfo = getStatusInfo(vorgang.status);
  const kategorieInfo = getKategorieInfo(vorgang.kategorie);
  const prioritaetInfo = getPrioritaetInfo(vorgang.prioritaet);
  const istUeberfaellig = vorgang.frist && new Date(vorgang.frist) < new Date() && vorgang.status !== 'abgeschlossen';

  return (
    <div
      onClick={onClick}
      className={`px-4 py-3 border-b cursor-pointer transition-colors ${
        isSelected ? 'bg-teal-50 border-l-2 border-l-teal-600' : 'hover:bg-gray-50 border-l-2 border-l-transparent'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{vorgang.titel}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusInfo.color}`}>
              <StatusIcon status={vorgang.status} />
              {statusInfo.label}
            </span>
            <span className="text-[10px] text-gray-400">{kategorieInfo.label}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 ml-2">
          <span className={`text-[10px] font-medium ${prioritaetInfo.color}`}>
            {prioritaetInfo.label}
          </span>
          {istUeberfaellig && (
            <span className="text-[10px] text-red-600 font-medium">Überfällig!</span>
          )}
        </div>
      </div>
      {vorgang.beschreibung && (
        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{vorgang.beschreibung}</p>
      )}
      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-400">
        {vorgang.frist && (
          <span className="flex items-center gap-0.5">
            <Calendar className="w-3 h-3" /> {formatDate(vorgang.frist)}
          </span>
        )}
        {vorgang.betrag && (
          <span>{Number(vorgang.betrag).toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR</span>
        )}
        <span>{timeAgo(vorgang.updated_at)}</span>
      </div>
    </div>
  );
}

// ==================== HAUPTSEITE ====================

export default function VorgaengePage() {
  const { vorgaenge, loading, loadVorgaenge, createVorgang, updateVorgang, deleteVorgang } = useVorgaenge();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('alle');
  const [filterKategorie, setFilterKategorie] = useState('alle');

  useEffect(() => { loadVorgaenge(); }, [loadVorgaenge]);

  const selectedVorgang = useMemo(
    () => vorgaenge.find(v => v.id === selectedId) || null,
    [vorgaenge, selectedId]
  );

  const filteredVorgaenge = useMemo(() => {
    let result = vorgaenge;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(v =>
        v.titel.toLowerCase().includes(q) ||
        v.beschreibung?.toLowerCase().includes(q)
      );
    }
    if (filterStatus !== 'alle') {
      result = result.filter(v => v.status === filterStatus);
    }
    if (filterKategorie !== 'alle') {
      result = result.filter(v => v.kategorie === filterKategorie);
    }
    return result;
  }, [vorgaenge, searchQuery, filterStatus, filterKategorie]);

  // Statistiken
  const stats = useMemo(() => ({
    offen: vorgaenge.filter(v => v.status === 'offen').length,
    inBearbeitung: vorgaenge.filter(v => v.status === 'in_bearbeitung').length,
    warten: vorgaenge.filter(v => v.status === 'warten').length,
    abgeschlossen: vorgaenge.filter(v => v.status === 'abgeschlossen').length,
    ueberfaellig: vorgaenge.filter(v => v.frist && new Date(v.frist) < new Date() && v.status !== 'abgeschlossen').length,
  }), [vorgaenge]);

  const handleCreate = async (form: NeuerVorgang) => {
    const result = await createVorgang(form);
    if (result) {
      toast.success('Vorgang erstellt');
      setShowModal(false);
      await loadVorgaenge();
      setSelectedId(result.id);
    }
  };

  const handleUpdate = async (id: string, updates: Partial<Vorgang>) => {
    const success = await updateVorgang(id, updates);
    if (success) await loadVorgaenge();
    return success;
  };

  const handleDelete = async (id: string) => {
    const success = await deleteVorgang(id);
    if (success) {
      setSelectedId(null);
      await loadVorgaenge();
    }
    return success;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Vorgänge & Projekte</h1>
            <p className="text-sm text-gray-500 mt-0.5">Problemfälle, Forderungen und Sondervorgänge verwalten</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">Neuer Vorgang</span>
          </button>
        </div>

        {/* Statistik-Leiste */}
        <div className="flex gap-4 mt-4">
          {[
            { label: 'Offen', count: stats.offen, color: 'text-blue-600 bg-blue-50', filter: 'offen' },
            { label: 'In Bearbeitung', count: stats.inBearbeitung, color: 'text-yellow-700 bg-yellow-50', filter: 'in_bearbeitung' },
            { label: 'Warten', count: stats.warten, color: 'text-orange-600 bg-orange-50', filter: 'warten' },
            { label: 'Abgeschlossen', count: stats.abgeschlossen, color: 'text-green-600 bg-green-50', filter: 'abgeschlossen' },
            { label: 'Überfällig', count: stats.ueberfaellig, color: 'text-red-600 bg-red-50', filter: '' },
          ].map(stat => (
            <button
              key={stat.label}
              onClick={() => stat.filter && setFilterStatus(filterStatus === stat.filter ? 'alle' : stat.filter)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${stat.color} ${
                filterStatus === stat.filter ? 'ring-2 ring-offset-1 ring-current' : ''
              }`}
            >
              <span className="font-bold">{stat.count}</span>
              <span className="font-medium">{stat.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content: Liste + Detail */}
      <div className="flex-1 flex min-h-0">
        {/* Linke Spalte: Liste */}
        <div className="w-[380px] flex-shrink-0 border-r flex flex-col bg-white">
          {/* Suche & Filter */}
          <div className="flex-shrink-0 p-3 border-b space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Vorgänge durchsuchen..."
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterKategorie}
                onChange={e => setFilterKategorie(e.target.value)}
                className="text-xs border rounded-lg px-2 py-1.5 flex-1"
              >
                <option value="alle">Alle Kategorien</option>
                {VORGANG_KATEGORIEN.map(k => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Vorgänge-Liste */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredVorgaenge.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Briefcase className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm font-medium">Keine Vorgänge gefunden</p>
                <button
                  onClick={() => setShowModal(true)}
                  className="mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                  Ersten Vorgang erstellen
                </button>
              </div>
            ) : (
              filteredVorgaenge.map(v => (
                <VorgangKarte
                  key={v.id}
                  vorgang={v}
                  isSelected={selectedId === v.id}
                  onClick={() => setSelectedId(v.id)}
                />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 px-4 py-2 border-t bg-gray-50 text-xs text-gray-400">
            {filteredVorgaenge.length} von {vorgaenge.length} Vorgängen
          </div>
        </div>

        {/* Rechte Spalte: Detail */}
        <div className="flex-1 min-w-0 bg-gray-50">
          {selectedVorgang ? (
            <VorgangDetail
              key={selectedVorgang.id}
              vorgang={selectedVorgang}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <Briefcase className="w-16 h-16 mb-4 opacity-20" />
              <p className="text-sm font-medium">Vorgang auswählen</p>
              <p className="text-xs mt-1">oder einen neuen Vorgang erstellen</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <NeuerVorgangModal
          onClose={() => setShowModal(false)}
          onSave={handleCreate}
        />
      )}
    </div>
  );
}
